import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ImportCurve,
  NotificationStatus,
  Computing,
  ImportCircle,
  CardPos,
  Save2,
  TickCircle,
  Wallet,
  Trash,
  Calculator,
} from "iconsax-react";
import { Button, Badge, Dropdown, DropdownItem } from "@/shared/ui";
import { CheckIcon } from "@/shared/assets/icons";
import { getMonthName } from "@/shared/utils";
import type { Locale } from "@/shared/utils";
import type { CombinedStatus } from "../../../types";

type ActiveTab = "step1" | "step2" | "step3";

interface Props {
  month: number;
  year: number;
  locale: Locale;
  status: "draft" | "approved";
  combinedStatus: CombinedStatus;
  activeTab: ActiveTab;
  canEdit: boolean;
  canApprove: boolean;
  hasChanges: boolean;
  isSaving: boolean;
  isApproving: boolean;
  isApproveDisabled: boolean;
  isAnyLoading: boolean;
  onSave: () => void;
  onApprove: () => void;
  onDownloadPdf: () => void;
  onDownloadDocx: () => void;
  hasPayroll: boolean;
  canGeneratePayroll: boolean;
  onGeneratePayroll: () => void;
  isGenerating: boolean;
  canApprovePayroll: boolean;
  onApprovePayroll: () => void;
  isApprovingPayroll: boolean;
  canMarkPaid: boolean;
  onMarkPaid: () => void;
  isMarkingPaid: boolean;
  canDeletePayroll: boolean;
  onDeletePayroll: () => void;
  isDeletingPayroll: boolean;
  onDeleteTimesheet: () => void;
  isDeletingTimesheet: boolean;
}

const COMBINED_STATUS_CONFIG: Record<
  CombinedStatus,
  { color: "gray" | "positive" | "info" | "notice" | "primary"; labelKey: string; icon: React.ReactNode }
> = {
  draft: {
    color: "gray",
    labelKey: "combinedStatus.draft",
    icon: <NotificationStatus size={16} color="currentColor" />,
  },
  timesheet_approved: {
    color: "notice",
    labelKey: "combinedStatus.timesheet_approved",
    icon: <Computing size={16} color="currentColor" />,
  },
  calculated: {
    color: "info",
    labelKey: "combinedStatus.calculated",
    icon: <ImportCircle size={16} color="currentColor" />,
  },
  pending_payment: {
    color: "info",
    labelKey: "combinedStatus.pending_payment",
    icon: <CardPos size={16} color="currentColor" />,
  },
  paid: { color: "positive", labelKey: "combinedStatus.paid", icon: <CheckIcon /> },
};

export default function TimesheetDetailHeader({
  month,
  year,
  locale,
  combinedStatus,
  activeTab,
  canEdit,
  canApprove,
  hasChanges,
  isSaving,
  isApproving,
  isApproveDisabled,
  isAnyLoading,
  onSave,
  onApprove,
  onDownloadPdf,
  onDownloadDocx,
  hasPayroll,
  canGeneratePayroll,
  onGeneratePayroll,
  isGenerating,
  canApprovePayroll,
  onApprovePayroll,
  isApprovingPayroll,
  canMarkPaid,
  onMarkPaid,
  isMarkingPaid,
  canDeletePayroll,
  onDeletePayroll,
  isDeletingPayroll,
  onDeleteTimesheet,
  isDeletingTimesheet,
}: Props) {
  const { t } = useTranslation("TimesheetsPage");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const monthName = getMonthName(month, locale);
  const isDraft = combinedStatus === "draft";

  const statusConfig = COMBINED_STATUS_CONFIG[combinedStatus];

  const showTimesheetActions = activeTab !== "step3";
  const showPayrollActions = activeTab === "step3" && hasPayroll;

  return (
    <>
      <title>{t("meta.detailTitleFull", { month: monthName.toLowerCase(), year })}</title>
      <div className="flex items-center justify-between pt-1 pb-4 border-b surface-base-stroke mb-5 pr-1  ">
        <div className="flex items-center gap-4">
          <h1 className="text-display-xs content-base-primary">
            {activeTab === "step3"
              ? t("meta.payrollPageTitle", { month: monthName.toLowerCase(), year })
              : t("meta.pageTitle", { month: monthName.toLowerCase(), year })}
          </h1>
          <Badge variant="soft" color={statusConfig.color} text={t(statusConfig.labelKey)} icon={statusConfig.icon} />
        </div>
        <div className="flex items-center gap-2">
          {combinedStatus !== "draft" && activeTab !== "step3" && (
            <Dropdown open={isDropdownOpen} onClose={() => setIsDropdownOpen(false)}>
              <Button
                variant="secondary"
                size="lg"
                isIconButton
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                disabled={isAnyLoading}>
                <ImportCurve size={20} color="currentColor" />
              </Button>
              <DropdownItem onClick={onDownloadPdf}>{t("actions.downloadPdfOption")}</DropdownItem>
              <DropdownItem onClick={onDownloadDocx}>{t("actions.downloadDocx")}</DropdownItem>
            </Dropdown>
          )}

          {canGeneratePayroll && !hasPayroll && (
            <Button variant="primary" size="lg" onClick={onGeneratePayroll} disabled={isAnyLoading || isGenerating}>
              <Calculator size={16} color="currentColor" />
              {isGenerating ? t("actions.saving") : t("payroll.actions.generate")}
            </Button>
          )}

          {showTimesheetActions && (
            <>
              {isDraft && canEdit && (
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={onDeleteTimesheet}
                  disabled={isDeletingTimesheet || isAnyLoading}
                  className="text-red-500!"
                >
                  <Trash size={16} color="currentColor" />
                  {t("actions.delete")}
                </Button>
              )}
              {isDraft && canEdit && (
                <Button variant="secondary" size="lg" onClick={onSave} disabled={!hasChanges || isAnyLoading}>
                  <Save2 size={16} color="currentColor" />
                  {isSaving ? t("actions.saving") : t("actions.save")}
                </Button>
              )}
              {isDraft && canEdit && canApprove && (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={onApprove}
                  disabled={isAnyLoading || isApproveDisabled}
                  textDuringDisabled={isApproveDisabled ? t("actions.rules.cannotApprove") : undefined}>
                  <TickCircle size={16} color="currentColor" />
                  {isApproving ? t("actions.saving") : t("actions.approveTimesheet")}
                </Button>
              )}
            </>
          )}

          {showPayrollActions && (
            <>
              {canDeletePayroll && (
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={onDeletePayroll}
                  disabled={isDeletingPayroll || isAnyLoading}
                  className="text-red-500!">
                  <Trash size={16} color="currentColor" />
                  {t("payroll.actions.delete")}
                </Button>
              )}
              {canApprovePayroll && (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={onApprovePayroll}
                  disabled={isApprovingPayroll || isAnyLoading}>
                  <TickCircle size={16} color="currentColor" />
                  {t("payroll.actions.approve")}
                </Button>
              )}
              {canMarkPaid && (
                <Button variant="primary" size="lg" onClick={onMarkPaid} disabled={isMarkingPaid || isAnyLoading}>
                  <Wallet size={16} color="currentColor" />
                  {t("payroll.actions.markPaid")}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

